import { Loader2 } from "lucide-react";

import { formatRetryWait } from "~/utils/turnstile-error";

import { TurnstileSlot } from "./Turnstile";
import { VerificationNotice } from "./VerificationNotice";

import type { ReactNode } from "react";
import type { AuthGuard } from "~/hooks/useAuthGuard";

/**
 * Las piezas visibles del antibot que comparten los cinco formularios de ingreso. La lógica está
 * en `useAuthGuard`; acá sólo se dibuja.
 */

type AuthGuardSlotProps = {
  guard: AuthGuard;
  /** El mismo envío del formulario: es lo que hace "Reintentar" si el nivel con clic falla. */
  onRetry?: () => void;
  noticeClassName?: string;
  /**
   * Mostrar "Verificando…" acá mientras se espera la constancia. Para el paso del código: ahí se
   * reenvía con un link, no con el botón que ya lo dice.
   */
  showProgress?: boolean;
};

/**
 * El widget (invisible o con clic), y debajo el aviso del último rechazo con su cuenta regresiva.
 * Va donde iba `<Turnstile />`: entre los campos y el botón. Devuelve hermanos, no un contenedor,
 * para que el formulario espacie el aviso igual que el resto de sus filas.
 */
export function AuthGuardSlot({
  guard,
  onRetry,
  noticeClassName,
  showProgress = false,
}: AuthGuardSlotProps) {
  return (
    <>
      {showProgress && guard.verifying && !guard.turnstile.snapshot.interaction && (
        <p
          role="status"
          className="flex items-center justify-center gap-2 text-[13px] text-landing-muted"
        >
          <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
          Verificando…
        </p>
      )}
      <TurnstileSlot turnstile={guard.turnstile} onRetry={onRetry} />
      {guard.notice && (
        <VerificationNotice
          title={guard.notice.title}
          body={guard.notice.body}
          secondsLeft={guard.retrySecondsLeft}
          totalSeconds={guard.retryTotalSeconds}
          className={noticeClassName}
        />
      )}
    </>
  );
}

type AuthGuardButtonLabelProps = {
  guard: AuthGuard;
  /** Lo que dice el botón en reposo ("Enviar código", "Continuar"…). */
  idle: ReactNode;
  /** Lo que dice mientras sale el pedido. */
  sending?: ReactNode;
  /** El formulario tiene su propio "en curso" (p. ej. creando la cuenta después del código). */
  pending?: boolean;
};

/**
 * El texto del botón de envío. Mientras se espera la constancia dice "Verificando…" con un
 * spinner, sin ningún cartel rojo: la persona no hizo nada mal, sólo hay que esperar un segundo.
 */
export function AuthGuardButtonLabel({
  guard,
  idle,
  sending = "Enviando…",
  pending = false,
}: AuthGuardButtonLabelProps) {
  if (guard.verifying) {
    return (
      <span className="inline-flex items-center justify-center gap-2">
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        Verificando…
      </span>
    );
  }
  if (guard.busy || pending) return <>{sending}</>;
  if (guard.retrySecondsLeft > 0) {
    return <>{`Probá de nuevo en ${formatRetryWait(guard.retrySecondsLeft)}`}</>;
  }
  return <>{idle}</>;
}

/** El botón no se puede tocar mientras hay un envío en curso o corre una espera. */
export function isAuthGuardLocked(guard: AuthGuard) {
  return guard.busy || guard.retrySecondsLeft > 0;
}
